import React from 'react';
import PropTypes from 'prop-types';
import gettext from '@eb/gettext';
import has from 'lodash/has';

import { withMainControls } from '@eb/eds-structure';
import { TYPE_ERROR } from '@eb/eds-notification';
import { Key } from '@eb/eds-iconography';
import { AlertChunky } from '@eb/eds-iconography';

import SetPasswordForm from '../../components/forms/SetPasswordForm';
import LookUpForm from '../../components/forms/LookUpForm';
import PageHeader from '../../components/PageHeader';
import AuthenticationStructure from '../../components/AuthenticationStructure';

import { PASSWORD_SET_TOKEN } from '../../constants';

/**
 * List of urls that will lead to this page:
 * * BASE_URL/RESET_PASSWORD_PATH
 */
class ResetPasswordPage extends React.Component {
    static propTypes = {
        handleResetPassword: PropTypes.func.isRequired,
        handleSetPassword: PropTypes.func.isRequired,
        locale: PropTypes.string,
        hideMainNotification: PropTypes.func,
        addMainNotification: PropTypes.func,
    };

    _handleShowNotification = (error) => {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (addMainNotification && !this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });

            this._hasNotification = true;
        }
    };

    _handleClearNotification = () => {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;

        hideMainNotification && hideMainNotification();
    };

    // Copy from LookUpPage. Probably an intermediate connected component should manage this.
    _handleOnSocialLogIn = (socialProvider, socialResponse) => {
        const {
            clearAuthNotification,
            onSocialLogInUser,
            socialSSOSettings,
        } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        // add param to call api, depending on feature flag
        let skipCreation = false;

        if (socialSSOSettings) {
            skipCreation = socialSSOSettings.twoStepsSignup || false;
        }

        if (onSocialLogInUser) {
            onSocialLogInUser(socialProvider, {
                skipCreation,
                ...socialResponse,
            });
        }
    };

    // Copy from LookUpPage. Probably an intermediate connected component should manage this.
    _handleOnSocialLogInNotAuthorized = (socialProvider, socialStatus) => {
        const { onSocialLogInUserNotAuthorized } = this.props;

        if (onSocialLogInUserNotAuthorized) {
            onSocialLogInUserNotAuthorized(socialProvider, socialStatus);
        }
    };

    render() {
        const {
            locale,
            router: {
                location: { query },
            },
            handleSetPassword,
            handleResetPassword,

            clearAuthNotification,
            socialSSOSettings,
            onComunityGuidelinesClick,
            onGetStartedClick,
            onPrivacyPolicyClick,
            onSocialProviderClick,
            onTermsOfServiceClick,
        } = this.props;

        let submitAction = handleSetPassword;
        let DynamicForm = SetPasswordForm;

        const headerText = gettext('Reset your password');
        const iconType = <Key />;
        let buttonCTA = gettext('Reset password');
        let subtitleText = gettext('Enter your new password below.');
        let shouldHideLegalCopy = true;
        let extraProps = {};

        // If the query includes paramters to reset the password, we trigger
        // the reset password form.
        // Not sure if this part is triggered in setPassword or resetPassword flow.
        if (!has(query, PASSWORD_SET_TOKEN)) {
            submitAction = handleResetPassword;
            DynamicForm = LookUpForm;
            buttonCTA = gettext('Send email');
            subtitleText = gettext(
                "Enter your email address and we'll send you a link to reset your password.",
            );
            shouldHideLegalCopy = false;

            extraProps = {
                clearAuthNotification,
                socialSSOSettings,
                onComunityGuidelinesClick,
                onGetStartedClick,
                onPrivacyPolicyClick,
                onSocialProviderClick,
                onTermsOfServiceClick,

                // Copy from LookUpPage. Probably an intermediate connected component should manage this.
                onSocialConnectVerified: this._handleOnSocialLogIn,
                onSocialConnectNotAuthorized: this
                    ._handleOnSocialLogInNotAuthorized,
            };
        }

        return (
            <AuthenticationStructure>
                <>
                    <PageHeader
                        headerText={headerText}
                        subtitleText={subtitleText}
                        iconType={iconType}
                    />

                    <DynamicForm
                        onSubmit={submitAction}
                        headerText={headerText}
                        iconType={iconType}
                        subtitleText={subtitleText}
                        buttonCTA={buttonCTA}
                        shouldHideLegalCopy={shouldHideLegalCopy}
                        locale={locale}
                        showNotification={this._handleShowNotification}
                        clearNotification={this._handleClearNotification}
                        {...extraProps}
                    />
                </>
            </AuthenticationStructure>
        );
    }
}

export default withMainControls(ResetPasswordPage);
